<script setup lang="ts">
import { Location as LocationModel } from "@/models"
import { ref } from "vue";
import Combobox from "@/components/ui/Combobox.vue";
import { Dialog, DialogFooter, DialogHeader, DialogScrollContent, DialogTitle } from "@/components/ui/dialog";
import {
  CirclePlusIcon
} from '@lucide/vue'
import { Button } from "@/shadcn/components/ui/button";
import LocationSetting from "@/components/location/LocationSetting.vue";
const props = defineProps({
  modelValue: {
    type: String,
    required: false,
    default: null
  },
  id: {
    type: String,
    required: false,
    default: null
  },
  tourId: {
    type: Number,
    required: true
  },
  inputName: {
    type: String,
    required: false,
    default: undefined
  },
  countries: {
    type: Array,
    required: false,
    default: () => []
  }
});

const locations = ref([]);
const selectedLocationId = ref(<undefined | number>undefined);

LocationModel
  .where('tour_id', props.tourId)
  .get()
  .then((response) => {
    locations.value = response.data;
    selectedLocationId.value = props.modelValue;
  });

const newLocation = ref({
  name: '',
  address: '',
  latitude: null,
  longitude: null,
  tour_id: props.tourId
});

const isSavingLocation = ref(false);
const creationErrors = ref<{ [key: string]: string[] }>({});
const showAddDialog = ref(false);
function onDialogUpdate(open: boolean) {
  if (!open) {
    // Reset new location data
    newLocation.value = {
      name: '',
      address: '',
      latitude: null,
      longitude: null,
      tour_id: props.tourId
    };
    // Clear errors
    creationErrors.value = {};
  }
  showAddDialog.value = open;
}
const saveLocation = (event: Event) => {
  event.preventDefault();
  const location = new LocationModel({
    ...newLocation.value,
    tour_id: props.tourId,
  })
  isSavingLocation.value = true;
  location.save()
    .then((newLocationResponse) => {
      LocationModel
        .where('tour_id', props.tourId)
        .get()
        .then((response) => {
          locations.value = response.data;
          selectedLocationId.value = newLocationResponse.id
          showAddDialog.value = false;
        });
    })
    .catch((error) => {
      // Set a user-facing error message
      if (error.response?.data?.message) {
        creationErrors.value = error.response.data.message;
      } else {
        creationErrors.value = { general: ['An error occurred while saving the location. Please try again.'] };
      }
    })
    .finally(() => {
      isSavingLocation.value = false;
    });
}

</script>

<template>
  <div class="tw:flex tw:flex-center tw:gap-2">
    <Combobox
      :options="locations.map((location) => {
      return { value: location.id, label: location.name }
    })"
      :id="id"
      :fullwidth="true"
      v-model="selectedLocationId"
    />
    <div>
      <Button variant="secondary" type="button" @click="showAddDialog = true">
        <CirclePlusIcon class="tw:h-5 tw:w-5 tw:text-zinc-500 tw:cursor-pointer" />
        {{ $t('Add') }}
      </Button>
    </div>
  </div>

  <template v-if="props.inputName">
    <input type="hidden" :name="props.inputName" :value="selectedLocationId" />
  </template>

  <Dialog :open="showAddDialog"
          @update:open="onDialogUpdate">
    <DialogScrollContent
      size="lg"
      @interact-outside.prevent>
      <form @submit="saveLocation">

        <DialogHeader>
          <DialogTitle>
            {{ $t('New location') }}
          </DialogTitle>
        </DialogHeader>
        <LocationSetting
          :countries="props.countries"
          :errors="creationErrors"
          @update:location="(location) => {
            newLocation = {...location};
          }"
        />


        <DialogFooter>
          <Button variant="secondary" type="button" @click="showAddDialog = false">
            {{ $t('Cancel') }}
          </Button>
          <Button type="submit" :loading="isSavingLocation">
            {{ $t('Save') }}
          </Button>
        </DialogFooter>
      </form>
    </DialogScrollContent>
  </Dialog>

</template>
